import {useNavigate, useSearchParams} from "react-router-dom";
import {DataRequestParams, DataRequestState, DataTable, useDataRequest, useDebouncedFilter} from "../../data";
import FilterTableAufgaben, {AufgabenFilter} from "./FilterTableAufgaben";
import {useMemo} from "react";
import {aufgabenStyles} from "../../../styles/aufgaben/aufgaben.styles";
import {useAufgabenHandling} from "../../../hooks/aufgaben/useAufgabenHandling";
import {Box, Button, capitalize, Paper, Typography} from "@mui/material";
import {Aufgabe} from "../../../api";
import Layout from "../../Layout";
import {GridColDef} from "@mui/x-data-grid";
import {permissions} from "../../../permissions";
import MultipleGuard from "../../guards/MultipleGuard";

export default function TableAufgaben() {
    const [searchParams] = useSearchParams();
    const {translation, loadAufgaben} = useAufgabenHandling();
    const navigate = useNavigate();

    const request: DataRequestState<AufgabenFilter> = useDataRequest<AufgabenFilter>({
        filter: {
            filtered: searchParams.get("filter") ?? "",
            search: searchParams.get("search") ?? "",
        },
    });

    const [{ search }, setField] = useDebouncedFilter(request);

    const filter = useMemo(() => {
        return searchParams.get("filter");
    }, [searchParams]);

    return (
        <Layout>
            <Paper sx={aufgabenStyles.tableAufgaben.paper}>
                <Box sx={aufgabenStyles.tableAufgaben.headerBoxWrapper}>
                    <Typography variant="h5">
                        {capitalize(translation('tasks'))}
                    </Typography>
                    <MultipleGuard
                        permissions={[
                            permissions.aufgaben.create,
                            permissions.aufgaben.own_create,
                        ]}
                    >
                        <Button
                            variant={"contained"}
                            color={"secondary"}
                            onClick={() => {
                                navigate("/aufgaben/new");
                            }}
                        >
                            {capitalize(translation('task-create'))}
                        </Button>
                    </MultipleGuard>
                </Box>
                <FilterTableAufgaben setField={setField} search={search}/>
                <Box sx={aufgabenStyles.tableAufgaben.box}>
                    <DataTable<Aufgabe, AufgabenFilter>
                        name="table-aufgaben"
                        queryKey={["aufgaben"]}
                        loadData={(params: DataRequestParams<AufgabenFilter>) => loadAufgaben(params, request.filter)}
                        request={request}
                        setField={setField}
                        columns={useColumns()}
                        size="large"
                        initialFilters={filter ? JSON.parse(filter) : undefined}
                        onRowClick={(row, event) => {
                            if (event.ctrlKey || event.metaKey) {
                                event.preventDefault();
                                window.getSelection()?.removeAllRanges();
                                window.open(`/aufgaben/${row.id}`, "_blank");
                            } else {
                                navigate(`/aufgaben/${row.id}`);
                            }
                        }}
                        disableRowSelectionOnClick={true}
                    />
                </Box>
            </Paper>
        </Layout>
    );
}

function useColumns(): Array<GridColDef<Aufgabe>> {
    const {translation} = useAufgabenHandling();

    return useMemo(
        () => [
            {
                field: "titel",
                headerName: capitalize(translation("task-attributes-title")),
                flex: 1,
            },
            {
                field: "beschreibung",
                headerName: capitalize(translation("task-attributes-description")),
                flex: 1,
            },
            {
                field: "stimmeId",
                headerName: capitalize(translation("task-attributes-voice")),
                flex: 1,
            },
            {
                field: "mandantId",
                headerName: capitalize(translation("task-attributes-client")),
                flex: 1,
            },
            {
                field: "status",
                headerName: capitalize(translation("task-attributes-task-status")),
                flex: 1,
                valueFormatter: (value) =>
                    value ? capitalize(String(value).toLowerCase()) : "",
            },
        ],
        [translation]
    );
}
